import { useTranslation } from 'react-i18next';
import { useChatScrollAnchor } from '@/features/ai-chat/lib/useChatScrollAnchor';
import { MessageBubble } from '@/shared/ui';
import type { ChatMessage } from '@/widgets/right-side-menu/lib/chat-types';
import { AssistantMessage } from './AssistantMessage';
import { ChatInput } from './ChatInput';
import { LoadingMessage } from './LoadingMessage';

type Props = {
  messages: ChatMessage[];
  isLoading: boolean;
  onSend: (message: string) => void;
  /** トークン上限超過時。入力欄を TokenLimitExceededView に差し替える */
  isTokenLimitExceeded?: boolean;
};

/** チャットスレッド表示。メッセージ一覧（ユーザー/AI応答）と入力欄を縦に配置する */
export const ChatThreadView = ({ messages, isLoading, onSend, isTokenLimitExceeded }: Props) => {
  const { t } = useTranslation();
  // messages は常に [user, assistant, user, assistant, ...] の順なので、末尾が
  // assistant のときだけがアンカー対象。末尾が user のときは AI 応答待ち中。
  const lastMessage = messages.at(-1);
  const lastAssistantId = lastMessage?.role === 'assistant' ? lastMessage.id : undefined;
  const { lastAssistantRef, bottomMarkerRef } = useChatScrollAnchor({
    messageCount: messages.length,
    lastAssistantId,
    isLoading,
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto pb-4">
        <div className="flex flex-col gap-6 pt-4">
          {messages.map((message) =>
            message.role === 'user' ? (
              <div key={message.id} className="pl-12">
                <MessageBubble message={message.content} position="right" />
              </div>
            ) : (
              <div
                key={message.id}
                ref={message.id === lastAssistantId ? lastAssistantRef : undefined}
              >
                <AssistantMessage content={message.content} projectPrompt={message.projectPrompt} />
              </div>
            ),
          )}
          {isLoading && <LoadingMessage />}
          <div ref={bottomMarkerRef} aria-hidden />
        </div>
      </div>

      <div className="shrink-0 pb-4">
        <ChatInput
          onSend={onSend}
          waiting={isLoading}
          placeholder={t('ai.chat.input-placeholder-thread')}
          isTokenLimitExceeded={isTokenLimitExceeded}
        />
      </div>
    </div>
  );
};
